<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <div class="page-content">
      <AppBreadcrumb :items="breadcrumbItems" />

      <!-- Topic Selection -->
      <template v-if="!started">
        <div class="topic-header">
          <div class="topic-header-info">
            <h2 class="topic-title">{{ topicName }}</h2>
            <p class="topic-desc">{{ topicDescription }}</p>
            <div class="topic-stats">
              <span class="stat-badge">
                <span class="stat-icon">📝</span>
                {{ totalQuestions }} ta savol mavjud
              </span>
            </div>
          </div>
        </div>

        <!-- Count selector -->
        <div class="count-selector">
          <h3 class="selector-title">Savollar sonini tanlang</h3>
          <div class="count-options">
            <button v-for="opt in countOptions" :key="opt" class="count-btn" :class="{ active: selectedCount === opt }"
              @click="selectedCount = opt">
              {{ opt }} ta
            </button>
          </div>
          <a-button type="primary" size="large" @click="startTest" :loading="starting" class="start-btn">
            🚀 Testni boshlash
          </a-button>
        </div>
      </template>

      <!-- Test Room -->
      <template v-else>
        <RealExamRoom :title="topicName" :questions="questions" :result-id="resultId"
          :back-route="`/topic-test/${topicId}`" @finish="handleFinish" />
      </template>
    </div>
  </div>
</template>

<script>
import HeaderComponent from '../components/HeaderComponent.vue'
import AppBreadcrumb from '../components/AppBreadcrumb.vue'
import RealExamRoom from '../components/RealExamRoom.vue'
import api from '@/api/axios'
import { message } from 'ant-design-vue'

export default {
  name: 'TopicTestView',
  components: { HeaderComponent, AppBreadcrumb, RealExamRoom },
  data() {
    return {
      topicId: null,
      topicName: '',
      topicDescription: '',
      totalQuestions: 0,
      selectedCount: 20,
      started: false,
      starting: false,
      questions: [],
      resultId: null,
    }
  },
  computed: {
    breadcrumbItems() {
      return [
        { label: 'Bosh sahifa', to: '/' },
        { label: this.topicName || 'Mavzu testi' }
      ]
    },
    countOptions() {
      const opts = [10, 20, 30, 50]
      return opts.filter(o => o <= this.totalQuestions)
    }
  },
  watch: {
    '$route.params.topicId': {
      immediate: true,
      handler(newId) {
        this.topicId = Number(newId)
        this.fetchTopicInfo()
      }
    }
  },
  methods: {
    async fetchTopicInfo() {
      try {
        const res = await api.get('/v1/topics/')
        const topics = res.data?.topics || []
        const topic = topics.find(t => t.id === this.topicId)
        if (topic) {
          this.topicName = topic.name
          this.topicDescription = topic.description
          this.totalQuestions = topic.question_count
          if (this.totalQuestions < this.selectedCount) {
            this.selectedCount = this.totalQuestions || 10
          }
        }
      } catch (err) {
        console.error('Topic info error:', err)
      }
    },

    async startTest() {
      this.starting = true
      try {
        const res = await api.post('/v1/tests/start-topic/', {
          topic_id: this.topicId,
          count: this.selectedCount
        })
        if (res.data) {
          this.questions = res.data.questions
          this.resultId = res.data.result.id
          this.started = true
          message.success(`${this.topicName} testi boshlandi!`)
        }
      } catch (err) {
        console.error('Start topic test error:', err)
        message.error(err.response?.data?.detail || 'Testni boshlashda xatolik')
      } finally {
        this.starting = false
      }
    },

    handleFinish(result) {
      console.log('Topic test finished:', result)
    }
  }
}
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
}

.page-content {
  flex: 1;
  padding: 0 24px 32px;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
}

.topic-header {
  margin-top: 16px;
  padding: 28px 24px;
  background: var(--bg-card, #11243d);
  border: 1px solid var(--border);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.topic-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #f59e0b);
}

.topic-title {
  font-size: 24px;
  font-weight: 800;
  color: var(--text);
  margin: 0 0 6px;
}

.topic-desc {
  font-size: 14px;
  color: var(--text-soft);
  margin: 0 0 16px;
}

.topic-stats {
  display: flex;
  gap: 12px;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
}

.stat-icon {
  font-size: 16px;
}

/* Count selector */
.count-selector {
  margin-top: 24px;
  padding: 24px;
  background: var(--bg-card, #11243d);
  border: 1px solid var(--border);
  border-radius: 20px;
}

.selector-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 16px;
}

.count-options {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.count-btn {
  padding: 10px 24px;
  border-radius: 12px;
  border: 2px solid var(--border);
  background: transparent;
  color: var(--text);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
}

.count-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.count-btn.active {
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
}

.start-btn {
  width: 100%;
  height: 50px !important;
  border-radius: 14px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  background: linear-gradient(135deg, #3b82f6, #6366f1) !important;
  border: none !important;
}

.start-btn:hover {
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4) !important;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .page-content {
    padding: 0 16px 24px;
  }

  .topic-header {
    padding: 20px 16px;
  }

  .topic-title {
    font-size: 20px;
  }

  .count-selector {
    padding: 16px;
  }

  .count-btn {
    padding: 8px 18px;
    font-size: 14px;
  }
}
</style>
